import React from 'react';
import {useMemo} from "react";
import {htmlDecode} from "../../../utils/htmlDecode";
import {Table, Thead, Tbody, Tr, Th, Td, chakra, Flex, Text} from '@chakra-ui/react'
import {TriangleDownIcon, TriangleUpIcon} from '@chakra-ui/icons'
import {useTable, useSortBy} from 'react-table'
import {useNavigate} from "react-router-dom";

const mapping = [
    {title: 'Year Funded', accessor: 'first_funded_year'},
    {title: 'Company', accessor: 'company_name'},
    {title: 'Description', accessor: 'description'},
    {title: 'Company Status', accessor: 'status'},
    {title: 'Sector', accessor: 'sectors'},
    {title: 'First Investment', accessor: 'first_funded_round'},
]

const headers = [
    {children: 'Year Funded', maxW: 90, display: ['none', 'table-cell']},
    {
        children: 'Company',
        maxW: 180,
        fontSize: 'lg',
        lineHeight: '4',
        fontWeight: 'semibold',
        textColor: '#333333',
        fontFamily: 'MarkWebMedium'
    },
    {
        children: 'Description',
        maxW: 400,
        display: ['none', 'table-cell'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    {children: 'Company Status', maxW: 87, display: ['none', 'table-cell']},
    {children: 'Sector', maxW: 150, display: ['none', 'table-cell']},
    {children: 'First Investment', maxW: 92, display: ['none', 'table-cell']},
];

export const SortableTable = ({companies}) => {
    const navigate = useNavigate();
    const data = useMemo(
        () => companies.map((company) => {
            return {
                ...company,
                description: company.short_description || company.long_description,
                status: company?.investment_status?.length > 0
                    ? company.investment_status[0].name
                    : '',
                sectors: company?.investment_sectors?.length > 0
                    ? company?.investment_sectors.map((i) => htmlDecode(i.name)).join(', ')
                    : '',
            }
        }),
        [companies],
    )


    const columns = useMemo(
        () => headers.map((header) => {
            return {
                Header: header.children,
                accessor: mapping.find((key) => key.title === header.children).accessor,
                maxW: header.maxW,
                fontWeight: header.fontWeight,
                fontFamily: header.fontFamily,
                fontSize: header.fontSize,
                textColor: header.textColor,
                display: header.display,
                lineHeight: header.lineHeight
            }
        }),
        [],
    )

    const {getTableProps, getTableBodyProps, headerGroups, rows, prepareRow} =
        useTable({columns, data}, useSortBy)

    return (
        <Table {...getTableProps()}>
            <Thead border={'none'}>
                {headerGroups.map((headerGroup) => (
                    <Tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => {
                            return (
                                <Th
                                    {...column.getHeaderProps({
                                        ...column.getSortByToggleProps(),
                                        style: {maxWidth: column.maxW, width: column.maxW},
                                    })}
                                    isNumeric={column.isNumeric}
                                    borderColor={'brandAlpha.200'}
                                    textColor={'brand.700'}
                                    maxW={column.maxW}
                                    w={column.maxW}
                                    display={column.display}
                                >
                                    <Flex alignItems="center">
                                        {column.render('Header')}
                                        <chakra.span pl='4'>
                                            {column.isSorted ? (
                                                column.isSortedDesc ? (
                                                    <TriangleDownIcon aria-label='sorted descending'/>
                                                ) : (
                                                    <TriangleUpIcon aria-label='sorted ascending'/>
                                                )
                                            ) : null}
                                        </chakra.span>
                                    </Flex>
                                </Th>
                            )
                        })}
                    </Tr>
                ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
                {rows.map((row) => {
                    prepareRow(row)
                    return (
                        <Tr {...row.getRowProps()} cursor="pointer" onClick={() => {
                            navigate(`/portfolio/${row.original.slug}`);
                        }}>
                            {row.cells.map((cell) => {
                                return (
                                    <Td {...cell.getCellProps({
                                        style: {
                                            maxWidth: cell.column.maxW,
                                            width: cell.column.maxW,
                                            fontFamily: cell.column.fontFamily,
                                            display: cell.column.display
                                        },
                                    })} display={cell.column.display} isNumeric={cell.column.isNumeric}
                                        maxW={cell.column.maxW} w={cell.column.maxW}>
                                        <Text fontSize={cell.column?.fontSize || 'sm'}
                                              fontFamily={cell.column.fontFamily}
                                              fontWeight={cell.column.fontWeight}
                                              lineHeight={cell.column.lineHeight || '3'}
                                              noOfLines={1}
                                              textColor={'#333333'}>{cell.render('Cell')}</Text>
                                    </Td>
                                )
                            })}
                        </Tr>
                    )
                })}
            </Tbody>
        </Table>
    )
}